import styled from '@emotion/styled';
import {media, Spacer} from '@nfq/react-grid';

import {PayloadImage} from 'UI/components/layout/PayloadImage';

import {ContactColumn} from 'UI/modules/LachenschmidLayout/ContactColumn';
import {LinksColumn} from 'UI/modules/LachenschmidLayout/LinksColumn';

import type {FooterColumn as FooterColumnType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The footer column data object containing all necessary information for rendering the column layout.
     * This includes the column type (contact or links), image column data with image information, and any additional content properties.
     * The column object determines both the visual appearance and the type of content that will be displayed in the footer section.
     */
    column: FooterColumnType;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'FooterColumn'
     */
    testId?: string;
}

/**
 * The FooterColumn component renders a footer column layout with an image and conditional content based on column type.
 * It displays a PayloadImage alongside either ContactColumn or LinksColumn content depending on the column type property.
 * The component uses a flexible wrapper layout with proper spacing between the image and content sections.
 *
 * @param props        The component props.
 * @param props.column The footer column data containing image, type, and content information.
 * @param props.testId Test identifier for component testing purposes.
 * @returns The FooterColumn component JSX element.
 *
 * @example
 * ```tsx
 * <FooterColumn
 *     column={{
 *         type: 'contact',
 *         imageColumn: { image: { url: '/footer-logo.png', alt: 'Logo' } },
 *         // ... other column properties
 *     }}
 *     testId="footer-column"
 * />
 * ```
 */
const FooterColumn = ({column, testId = 'FooterColumn'}: ComponentProps) => (
    <Wrapper data-cy={testId}>
        <FooterImage image={column.imageColumn.image} />
        <Spacer maxX={23} x={4} />
        {column.type === 'contact' && (<ContactColumn column={column} />)}
        {column.type === 'links' && (<LinksColumn column={column} />)}
    </Wrapper>
);

FooterColumn.displayName = 'FooterColumn';

export {FooterColumn};

const Wrapper = styled.div`
    display: grid;
    grid-column: 2 / 3;
    grid-row: span 3;
    grid-template-columns: subgrid;
    grid-template-rows: subgrid;
    justify-items: start;

    ${media('sm')} {
        justify-items: center;
        grid-column: span 1;
        grid-row: span 3;
    }

    ${media('lg')} {
        grid-column: span 3;
        grid-row: span 1;
        justify-items: start;
    }
`;

const FooterImage = styled(PayloadImage)`
    max-width: 200px;
    min-width: 190px;

    picture:has(&) {
        justify-self: center;
    }

    ${media('lg')} {
        picture:has(&) {
            justify-self: start;
        }
    }
`;